import {WriteButton} from '../../../common/buttons/WriteActions';
import {useDelayedNameError, nameErrorHelperSx} from '../../../../hooks/useDelayedNameError';
import {validateCatalogName} from '../../../../common/catalogNameValidation';
import {mobileDialogSx} from '../../../common/dialogs/mobileDialogSx';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {PackageCreateRequest} from "../../../../models/Packages/IPackage";
import {useState} from "react";
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {UNITS} from "@mybake/ui/common/dictionary";
import SendIcon from "@mui/icons-material/Send";
import {packageAPI} from "../../../../services/PackageServices";
import {getApiErrorMessage, getApiFieldErrors} from '../../../../common/apiErrors';

interface Props {
    open: boolean;
    closeDialog: () => void;
}

export const CreatePackageDialog = ({open, closeDialog}: Props) => {
    const {t} = useAppTranslation();
    const [newPackage, setNewPackage] = useState<{name: string; unit: UNITS}>({
        name: '',
        unit: UNITS.NEW,
    });

    const [disableButton, setDisableButton] = useState<boolean>(false);
    const [createPackage, {
        error: savePackageError,
        isSuccess: savePackageIsSuccess
    }] = packageAPI.useCreatePackageMutation();

    const {
        data: packageNames,
        error: getNamesErrors,
        isFetching: namesFetching,
    } = packageAPI.useGetAvailablePackageNamesQuery(undefined, {
        skip: !open, refetchOnMountOrArgChange: true
    })

    const handleChangeName = (e: any) => {
        setNewPackage((prevState) => {
            return {
                ...prevState,
                name: e.target.value,
            };
        });
    }

    const nameValidation = validateCatalogName(newPackage.name, packageNames);
    const nameError = useDelayedNameError(newPackage.name, nameValidation === 'too_short' ? (newPackage.name ? t('common:name_min_length') : '') : nameValidation === 'duplicate' ? t('items:package_name_exists') : '');
    const disableSaveButton = !!nameValidation || !packageNames || namesFetching || !!getNamesErrors || newPackage.unit === UNITS.NEW;

    const handleSave = async () => {
        if (disableSaveButton || disableButton) return;
        setDisableButton(true);

        try {
            const body: PackageCreateRequest = {
                name: newPackage.name.trim(),
                unit: newPackage.unit as UNITS.PCS | UNITS.M,
                stocks: [],
                productTypes: [],
            };
            await createPackage(body).unwrap();
            closeDialog();
            setDisableButton(false);
        } catch (err) {
            console.error("Failed to save package:", err);
            setDisableButton(false);
            // The mutation state exposes this error in the form.
        }
    }

    return (
        <Dialog sx={mobileDialogSx} open={open} onClose={closeDialog} fullWidth maxWidth="xs" slotProps={{
            backdrop: {
                sx: {
                    backdropFilter: 'blur(6px)',
                    backgroundColor: 'rgba(0,0,0,0.4)',
                },
            },
        }}>
            <DialogTitle>{t('items:create_package')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    {getNamesErrors && <Alert severity="error">{getApiErrorMessage(getNamesErrors, t)}</Alert>}
                    <TextField
                        autoFocus
                        required
                        margin="dense"
                        id="name"
                        type="text"
                        label={t('items:package_name')}
                        fullWidth
                        value={newPackage.name}
                        onChange={handleChangeName}
                        error={!!nameError || !!getApiFieldErrors(savePackageError, t).name}
                        helperText={nameError || getApiFieldErrors(savePackageError, t).name || ' '} FormHelperTextProps={{sx: nameErrorHelperSx}}
                    />
                    <FormControl fullWidth required>
                        <InputLabel id="create-package-unit-label">
                            {t('items:unit_of_weight')}
                        </InputLabel>

                        <Select
                            labelId="create-package-unit-label"
                            label={t('items:unit_of_weight')}
                            value={newPackage.unit}
                            onChange={(e) =>
                                setNewPackage(prev => ({
                                    ...prev,
                                    unit: e.target.value as UNITS,
                                }))
                            }
                        >
                        {[UNITS.PCS, UNITS.M].map((unit) => (
                                <MenuItem key={unit} value={unit}>
                                    {unit}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {savePackageError && !getApiFieldErrors(savePackageError, t).name && <Typography color="error">{getApiErrorMessage(savePackageError, t)}</Typography>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2, gap: 1}}>
                <Button onClick={closeDialog} disabled={disableButton}>{t('common:cancel')}</Button>
                <WriteButton variant="contained"
                        onClick={handleSave}
                        endIcon={<SendIcon/>}
                        disabled={disableSaveButton || disableButton}
                >
                    {t('common:create')}
                </WriteButton>
            </DialogActions>
        </Dialog>
    )
}
